/** Typed arrays returned by geotiff.js readRasters({ interleave: true }). */
export type RasterTypedArray =
  | Float32Array
  | Float64Array
  | Int16Array
  | Int32Array
  | Uint8Array
  | Uint16Array
  | Uint32Array;

export type RasterTypedArrayName =
  | "Float32Array"
  | "Float64Array"
  | "Int16Array"
  | "Int32Array"
  | "Uint8Array"
  | "Uint16Array"
  | "Uint32Array";

export interface RasterTransferPayload {
  arrayType: RasterTypedArrayName;
  byteOffset: number;
  elementCount: number;
  rastersBuffer: ArrayBuffer;
}

const toTransferBuffer = (view: RasterTypedArray): ArrayBuffer => {
  const { buffer, byteLength, byteOffset } = view;
  if (
    buffer instanceof ArrayBuffer &&
    byteOffset === 0 &&
    byteLength === buffer.byteLength
  ) {
    return buffer;
  }

  return view.slice().buffer;
};

export const prepareRasterTransfer = (
  rasters: ArrayLike<number>,
): RasterTransferPayload => {
  if (ArrayBuffer.isView(rasters)) {
    const view = rasters as RasterTypedArray;
    const arrayType = view.constructor.name as RasterTypedArrayName;
    if (view.byteOffset === 0 && view.byteLength === view.buffer.byteLength) {
      return {
        arrayType,
        byteOffset: 0,
        elementCount: view.length,
        rastersBuffer: toTransferBuffer(view),
      };
    }

    const copy = view.slice();
    return {
      arrayType: copy.constructor.name as RasterTypedArrayName,
      byteOffset: 0,
      elementCount: copy.length,
      rastersBuffer: copy.buffer,
    };
  }

  const copy = Float32Array.from(rasters);
  return {
    arrayType: "Float32Array",
    byteOffset: 0,
    elementCount: copy.length,
    rastersBuffer: copy.buffer,
  };
};

export const rehydrateRasterTransfer = (
  payload: RasterTransferPayload,
): RasterTypedArray => {
  const { arrayType, byteOffset, elementCount, rastersBuffer } = payload;

  switch (arrayType) {
    case "Float32Array":
      return new Float32Array(rastersBuffer, byteOffset, elementCount);
    case "Float64Array":
      return new Float64Array(rastersBuffer, byteOffset, elementCount);
    case "Int16Array":
      return new Int16Array(rastersBuffer, byteOffset, elementCount);
    case "Int32Array":
      return new Int32Array(rastersBuffer, byteOffset, elementCount);
    case "Uint8Array":
      return new Uint8Array(rastersBuffer, byteOffset, elementCount);
    case "Uint16Array":
      return new Uint16Array(rastersBuffer, byteOffset, elementCount);
    case "Uint32Array":
      return new Uint32Array(rastersBuffer, byteOffset, elementCount);
    default:
      throw new Error(
        `Unsupported raster typed array: ${JSON.stringify(arrayType)}`,
      );
  }
};

/** Mapbox can't display GeoTIFFs directly — pack samples into PNG bytes. */
export const rastersToPngBytes = async (
  rasters: ArrayLike<number>,
  width: number,
  height: number,
  samplesPerPixel: number,
): Promise<ArrayBuffer> => {
  const canvas = new OffscreenCanvas(width, height);
  const context = canvas.getContext("2d");
  if (context == null) {
    throw new Error("Unable to create OffscreenCanvas context for GeoTIFF");
  }

  const imageData = context.createImageData(width, height);
  const pixelCount = width * height;

  if (samplesPerPixel === 4) {
    for (let index = 0; index < rasters.length; index += 1) {
      imageData.data[index] = Number(rasters[index] ?? 0);
    }
  } else if (samplesPerPixel === 3) {
    for (
      let sourceIndex = 0, targetIndex = 0;
      sourceIndex < rasters.length;
      sourceIndex += 3, targetIndex += 4
    ) {
      imageData.data[targetIndex] = Number(rasters[sourceIndex] ?? 0);
      imageData.data[targetIndex + 1] = Number(rasters[sourceIndex + 1] ?? 0);
      imageData.data[targetIndex + 2] = Number(rasters[sourceIndex + 2] ?? 0);
      imageData.data[targetIndex + 3] = 255;
    }
  } else if (samplesPerPixel === 1) {
    for (let index = 0; index < pixelCount; index += 1) {
      const value = Number(rasters[index] ?? 0);
      const offset = index * 4;
      imageData.data[offset] = value;
      imageData.data[offset + 1] = value;
      imageData.data[offset + 2] = value;
      imageData.data[offset + 3] = 255;
    }
  } else {
    throw new Error(`Unsupported GeoTIFF sample count: ${samplesPerPixel}`);
  }

  context.putImageData(imageData, 0, 0);
  const blob = await canvas.convertToBlob({ type: "image/png" });
  return blob.arrayBuffer();
};
